import { assign, setup } from 'xstate';

import { signOut } from '@/lib/auth';
import { queryClient } from '@/lib/query-client';
import { onboardingMachine } from '@/modules/onboarding/organization-setup/state/onboarding-machine';
import {
  AuthenticatedRoutes,
  CommonRoutes,
  OnboardingRoutes,
  UnauthenticatedRoutes,
} from '@/router/routes';
import { CommonInternalStates } from '@/router/state/root/component/common-internal-states';
import { RootContext } from '@/router/state/root/component/root-context';
import { LoggedOutRootEvent, RootEvent } from '@/router/state/root/component/root-event';
import { RootInput } from '@/router/state/root/component/root-input';
import { RootStates } from '@/router/state/root/component/root-states';
import { createStatesForRoutes } from '@/router/state/root/config/route-states-configurer';

export const rootStateMachine = setup({
  actors: {
    onboardingMachine: onboardingMachine,
  },
  types: {
    context: {} as RootContext,
    events: {} as RootEvent,
    input: {} as RootInput,
  },
  actions: {
    signOutCurrentUser: async ({ self }) => {
      await signOut(queryClient);
      self.send(new LoggedOutRootEvent());
    },
  },
}).createMachine({
  id: 'rootStateMachine',
  initial: RootStates.Initializing,
  context: ({ input }) => ({
    user: null,
    intendedRoute: null,
    route: null,
    isLoading: true,
    isColdStart: input.isColdStart,
  }),
  on: {
    INITIALIZED: [
      {
        target: `.${RootStates.Authenticated}`,
        guard: ({ event }) => !!event.user,
        actions: assign({
          isLoading: true,
          user: ({ event }) => event.user,
          intendedRoute: ({ event }) => event.intendedRoute,
        }),
      },
      {
        target: `.${RootStates.Unauthenticated}`,
        actions: assign({
          isLoading: true,
          intendedRoute: ({ event }) => event.intendedRoute,
        }),
      },
    ],
  },
  states: {
    [RootStates.Initializing]: {
      entry: assign({
        isLoading: true,
      }),
    },
    [RootStates.Unauthenticated]: {
      entry: assign({
        isLoading: false,
        isColdStart: false,
      }),
      initial: CommonInternalStates.Redirecting,
      states: createStatesForRoutes({
        routes: [...Object.values(UnauthenticatedRoutes), ...Object.values(CommonRoutes)],
        defaultRoute: UnauthenticatedRoutes.Login,
      }),
      on: {
        LOGGED_IN: {
          target: RootStates.Authenticated,
          actions: assign({
            isLoading: true,
            user: ({ event }) => event.user,
          }),
        },
      },
    },
    [RootStates.Authenticated]: {
      initial: 'onboarding',
      states: {
        onboarding: {
          invoke: {
            id: 'onboardingMachine',
            src: 'onboardingMachine',
            input: ({ context }) => ({
              intendedRoute: context.intendedRoute,
            }),
            onDone: {
              target: 'main',
            },
          },
          on: {
            INITIALIZED_ONBOARDING: {
              actions: assign({
                isLoading: false,
                isColdStart: false,
              }),
            },
          },
        },
        main: {
          /*// HARDCODED: Skip onboarding for testing
          entry: assign({
            isLoading: false,
            isColdStart: false,
          }),*/
          initial: CommonInternalStates.Redirecting,
          states: createStatesForRoutes({
            routes: [
              ...Object.values(AuthenticatedRoutes),
              ...Object.values(CommonRoutes),
              OnboardingRoutes.OnboardingCompletion, // Include completion route in main app
            ],
            defaultRoute: AuthenticatedRoutes.Planner,
          }),
        },
      },
      on: {
        LOGOUT_REQUESTED: {
          actions: [
            'signOutCurrentUser',
            assign({
              user: null,
              intendedRoute: null,
            }),
          ],
        },
        LOGGED_OUT: {
          target: RootStates.Unauthenticated,
        },
        NAVIGATE_TO_ROOT: {
          target: RootStates.Authenticated,
          actions: assign({
            intendedRoute: '/',
          }),
        },
      },
    },
  },
});
